import {
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@material-ui/core"
import { GetApp } from "@material-ui/icons"
import dayjs from "dayjs"
import PropTypes from "prop-types"
import React, { useContext, useEffect, useRef } from "react"
import AutoSizer from "react-virtualized-auto-sizer"
import BasicTable from "~/components/BasicTable"
import PermissionsWrapper from "~/components/Common/PermissionsWrapper"
import DebouncedSearch from "~/components/DebouncedSearch"
import LoaderButton from "~/components/LoaderButton/index.jsx"
import AppContext from "~/contexts/App"
import useOpenErrorSnackbar from "~/hooks/useOpenErrorSnackbar"

import Box from "../../Common/Box"
import useStyles from "./styles"

const AnalyticsTable = ({
  columns,
  data,
  mutation,
  downloadMutation,
  type,
  contentData,
  isFetching,
  fetchNextPage,
  refetch,
  hasNextPage,
  currentPage,
  setCurrentPage,
  pageSize,
  setPageSize,
  columnActions,
  setSearchValue = () => {},
  searchValue = "",
}) => {
  const classes = useStyles()
  const tableRef = useRef()

  const [anchorEl, setAnchorEl] = React.useState(null)

  const { openSnackBar } = useContext(AppContext)
  const openErrorSnackbar = useOpenErrorSnackbar()
  const openDownload = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const download = (type) => {
    setAnchorEl(null)
    if (type === "all" || type === "selected") {
      downloadMutation.mutate(
        type === "selected" ? { searchValue: searchValue } : {},
        {
          onSuccess: (res) => {
            openSnackBar({
              message: "Analytics data has been sent to your email",
            })
          },
          onError: (err) => {
            openErrorSnackbar(err, "Error loading analytics data")
          },
        },
      )
    }
  }

  useEffect(() => {
    if (!mutation.isLoading) {
      tableRef.current && tableRef.current.onQueryChange()
    }
  }, [searchValue])

  return (
    <Box className={classes.container} height="100%">
      {type === "course" ? (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
        >
          <Box display="flex">
            <Typography variant="subtitle1" className={classes.dataText}>
              Course Data:
            </Typography>
            <Typography variant="subtitle1" className={classes.dataText}>{`${
              contentData?.lesson_count || 0
            } lessons, `}</Typography>
            <Typography variant="subtitle1" className={classes.dataText}>
              {`${dayjs
                .duration(0, "s")
                .add(contentData?.duration, "seconds")
                .format("mm:ss")} duration `}
            </Typography>
          </Box>
          <Box marginLeft="16px" display="flex" alignItems="center">
            <Typography
              variant="subtitle1"
              className={classes.downloadText}
            >{`${
              data?.pages[0]?.total || 0
            } users took this ${type}`}</Typography>
            <PermissionsWrapper permissionGroup="admin">
              <Tooltip title="Download data" placement="top">
                <IconButton onClick={(e) => openDownload(e)}>
                  <GetApp color="secondary" />
                </IconButton>
              </Tooltip>
            </PermissionsWrapper>
            <Menu
              id="simple-menu"
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={download}
            >
              <MenuItem onClick={(e) => download("all")}>Download All</MenuItem>
              <MenuItem
                onClick={(e) => download("selected")}
                disabled={!searchValue || searchValue === ""}
              >
                Download Selected
              </MenuItem>
            </Menu>
            <LoaderButton
              color="secondary"
              working={mutation.isLoading}
              onClick={() => refetch()}
            >
              Reload Data
            </LoaderButton>
            <DebouncedSearch
              className={classes.search}
              placeholder="Search users"
              onChange={(value) => setSearchValue(value)}
            />
          </Box>
        </Box>
      ) : type === "lesson" ? (
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex">
            <Typography variant="subtitle1" className={classes.dataText}>
              Lesson Data:
            </Typography>
            <Typography variant="subtitle1" className={classes.dataText}>{`${
              contentData?.count || 0
            } knowledge check, `}</Typography>
            <Typography variant="subtitle1" className={classes.dataText}>
              {`${dayjs
                .duration(0, "s")
                .add(contentData?.video?.duration, "seconds")
                .format("mm:ss")} duration `}
            </Typography>
          </Box>
          <Box marginLeft="16px" display="flex" alignItems="center">
            <Typography
              variant="subtitle1"
              className={classes.downloadText}
            >{`${
              data?.pages[0]?.total || 0
            } users took this ${type}`}</Typography>
            <PermissionsWrapper permissionGroup="admin">
              <Tooltip title="Download data" placement="top">
                <IconButton onClick={(e) => openDownload(e)}>
                  <GetApp color="secondary" />
                </IconButton>
              </Tooltip>
            </PermissionsWrapper>
            <Menu
              id="simple-menu"
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={download}
            >
              <MenuItem onClick={(e) => download("all")}>Download All</MenuItem>
              <MenuItem
                onClick={(e) => download("selected")}
                disabled={!searchValue || searchValue === ""}
              >
                Download Selected
              </MenuItem>
            </Menu>
            <LoaderButton
              color="secondary"
              working={mutation.isLoading}
              onClick={() => refetch()}
            >
              Reload Data
            </LoaderButton>
            <DebouncedSearch
              className={classes.search}
              placeholder="Search users"
              onChange={(value) => setSearchValue(value)}
            />
          </Box>
        </Box>
      ) : type === "Learning Path" ? (
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex">
            <Typography variant="subtitle1" className={classes.dataText}>
              Learning Path Data:
            </Typography>
            <Typography variant="subtitle1" className={classes.dataText}>
              {contentData?.courses.length} Courses
            </Typography>
          </Box>
          <Box marginLeft="16px" display="flex" alignItems="center">
            <Typography variant="subtitle1" className={classes.downloadText}>
              {`${data?.pages[0].total || 0} users took this ${type}`}
            </Typography>
            <PermissionsWrapper permissionGroup="admin">
              <Tooltip title="Download data" placement="top">
                <IconButton onClick={(e) => openDownload(e)}>
                  <GetApp color="secondary" />
                </IconButton>
              </Tooltip>
            </PermissionsWrapper>
            <Menu
              id="simple-menu"
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={download}
            >
              <MenuItem onClick={(e) => download("all")}>Download All</MenuItem>
              <MenuItem
                onClick={(e) => download("selected")}
                disabled={!searchValue || searchValue === ""}
              >
                Download Selected
              </MenuItem>
            </Menu>
            <LoaderButton
              color="secondary"
              working={mutation.isLoading}
              onClick={() => refetch()}
            >
              Reload Data
            </LoaderButton>
            <DebouncedSearch
              className={classes.search}
              placeholder="Search users"
              onChange={(value) => setSearchValue(value)}
            />
          </Box>
        </Box>
      ) : type === "Certification" ? (
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex">
            <Typography variant="subtitle1" className={classes.dataText}>
              Certification Data:
            </Typography>
            <Typography variant="subtitle1" className={classes.dataText}>
              {contentData?.learning_paths.length} Learning Paths
            </Typography>
          </Box>
          <Box marginLeft="16px" display="flex" alignItems="center">
            <Typography variant="subtitle1" className={classes.downloadText}>
              {`${data?.pages[0].total || 0} users took this ${type}`}
            </Typography>
            <PermissionsWrapper permissionGroup="admin">
              <Tooltip title="Download data" placement="top">
                <IconButton onClick={(e) => openDownload(e)}>
                  <GetApp color="secondary" />
                </IconButton>
              </Tooltip>
            </PermissionsWrapper>
            <Menu
              id="simple-menu"
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={download}
            >
              <MenuItem onClick={(e) => download("all")}>Download All</MenuItem>
              <MenuItem
                onClick={(e) => download("selected")}
                disabled={!searchValue || searchValue === ""}
              >
                Download Selected
              </MenuItem>
            </Menu>
            <LoaderButton
              color="secondary"
              working={mutation.isLoading}
              onClick={() => refetch()}
            >
              Reload Data
            </LoaderButton>
            <DebouncedSearch
              className={classes.search}
              placeholder="Search users"
              onChange={(value) => setSearchValue(value)}
            />
          </Box>
        </Box>
      ) : (
        <div></div>
      )}

      <AutoSizer
        style={{ width: "100%", height: `60vh`, borderRadius: "50px" }}
      >
        {({ width, height }) => {
          return (
            <>
              <BasicTable
                columns={columns}
                data={data}
                isFetching={isFetching}
                fetchNextPage={fetchNextPage}
                refetch={refetch}
                hasNextPage={hasNextPage}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                columnActions={columnActions ? columnActions : null}
              />
            </>
          )
        }}
      </AutoSizer>
    </Box>
  )
}
AnalyticsTable.propTypes = {
  columns: PropTypes.array,
  data: PropTypes.object,
  loading: PropTypes.bool,
  mutation: PropTypes.object,
  downloadMutation: PropTypes.object,
  type: PropTypes.string,
  setSearchValue: PropTypes.func,
  searchValue: PropTypes.string,
  contentData: PropTypes.object,
  isFetching: PropTypes.bool,
  fetchNextPage: PropTypes.func,
  refetch: PropTypes.func,
  hasNextPage: PropTypes.bool,
  currentPage: PropTypes.number,
  setCurrentPage: PropTypes.func,
  pageSize: PropTypes.any,
  columnActions: PropTypes.func,
  setPageSize: PropTypes.func,
}

export default AnalyticsTable
